/**
 * Purpose: Message grouping utility for chat interface
 *
 * How it works:
 * 1. Analyzes message position within the array
 * 2. Compares sender with adjacent messages
 * 3. Determines group boundaries for visual separation
 *
 * Key features:
 * - Supports flex-col-reverse layout (newest at bottom)
 * - Properly groups messages by same sender
 * - Optimized for message list rendering
 */

import type { MessageEntity } from "@/indexedDB/types";
import { isDeletedSender } from "@/lib/deletedSender";

/**
 * MessageGrouping type definition
 *
 * @property message - The original message entity
 * @property isFirstInGroup - Whether this message is the first (topmost) in a visual group
 * @property isLastInGroup - Whether this message is the last (bottommost) in a visual group
 */
export type MessageGrouping = {
  message: MessageEntity;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
};

/**
 * Determines message grouping based on its position and sender.
 *
 * The messages array is ordered newest-first (index 0 = newest) and rendered
 * in a flex-col-reverse container, so visually:
 * - messages[index + 1] is the older neighbour, drawn ABOVE the current one
 * - messages[index - 1] is the newer neighbour, drawn BELOW the current one
 *
 * A message is "first in group" when it sits at the top of a visual cluster of
 * same-sender messages, and "last in group" when it sits at the bottom. At the
 * array boundaries the missing neighbour is `undefined`, which naturally
 * compares unequal to any sender, so the edge cases need no special handling.
 *
 * @param messages - Array of all messages (newest first)
 * @param index - Current message index
 * @returns Message with its grouping status
 */
export function getMessageGrouping(
  messages: MessageEntity[],
  index: number,
): MessageGrouping {
  const message = messages[index];

  // Anonymised senders (account deletion) all share one label, so two
  // DIFFERENT deleted users' adjacent messages would otherwise merge into a
  // single visual speaker. Force a group boundary whenever either side of a
  // pair is anonymised — noisier, but never attributes two people as one.
  const messageIsDeleted = isDeletedSender(message);
  const boundaryWith = (neighbour: MessageEntity | undefined): boolean =>
    !neighbour ||
    neighbour.sender !== message.sender ||
    messageIsDeleted ||
    isDeletedSender(neighbour);

  return {
    message,
    isFirstInGroup: boundaryWith(messages[index + 1]),
    isLastInGroup: boundaryWith(messages[index - 1]),
  };
}
